import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Joyride from 'react-joyride';
import center from '@turf/center';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

import CustomFormControl from '../../../shared/atoms/CustomFormControl';
import ActionButtonsPanel from '../../../shared/molecules/ActionButtonsPanel';
import UserGroupSelect from '../../../shared/molecules/UserGroupSelect';
import { useConfirmation } from '../../../../context/ConfirmationContext';
import MapBuildingsManagement from './MapBuildingsManagement';
import { DRAW_STYLES } from '../../../../constants/drawStyles';
import { buildingIconURL } from '../../../../utils/iconUrls';
import MapIconWrapper from '../../../MapIcon';
import DnDAssetsList from '../components/DnDAssetsList';
import { useFilePath } from '../../../../hooks/useFilePath';
import ActionButton from '../../../shared/atoms/ActionButton';
import { delCRUD } from '../../../../services/crudService';
import { useLocationsCacheContext } from '../../../../context/LocationsCacheContext';
import { queryClient } from '../../../../services/queryClient';
import { useGeneralContext } from '../../../../context/GeneralContext';
import { useAssetFormContext } from '../../../../context/AssetFormContext';

const ManageLocationForm = ({
  resetForm,
  initialValues,
  handleBlur,
  handleChange,
  setValues,
  values,
  touched,
  errors,
  isValid,
  dirty,
  submitForm,
  popPage,
  isSubmitting,
  setFieldTouched,
  setFieldValue,
}) => {
  const { t } = useTranslation();
  const { openConfirmation } = useConfirmation();
  const { openToast } = useGeneralContext();
  const { refreshMap } = useLocationsCacheContext();
  const { isLocationClone } = useAssetFormContext();
  const { buildIconPath } = useFilePath();
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(() => {
    return values.buildings.find(b => b.isInitialEditing) || null;
  });
  const [delLoading, setDelLoading] = useState(false);
  const buildingsRef = useRef(values.buildings);
  const iconElRef = useRef(new Map());
  const markerRef = useRef(new Map());
  const assetsRef = useRef(values.assets);
  const readyBuildingRef = useRef(null);

  useEffect(() => {
    buildingsRef.current = values.buildings;
  }, [values.buildings]);

  useEffect(() => {
    assetsRef.current = values.assets;
  }, [values.assets]);

  const steps = [
    {
      target: '.mapbox-gl-draw_polygon',
      content: t('tools_menu.location.draw'),
      disableBeacon: true,
      placement: 'left-start',
      styles: {
        options: {
          width: 200,
          zIndex: 1201,
        },
      },
    },
  ];

  const disablePolygonButton = disabled =>
    (document.querySelector('.mapbox-gl-draw_polygon').disabled = disabled);

  const centerCoordinates = geoJSON => {
    const centerGeoJson = center(geoJSON);

    if (!centerGeoJson.geometry) return [];

    return centerGeoJson.geometry.coordinates;
  };

  const handleLocationCreate = e => {
    if (e.features.length && e.features[0].geometry.type === 'Polygon') {
      disablePolygonButton(true);
      const centerCoords = centerCoordinates(e.features[0]);
      setFieldValue('coordinates', e.features[0].geometry.coordinates[0]);
      setFieldValue('lat', centerCoords[1]);
      setFieldValue('lng', centerCoords[0]);
    }
  };

  const drawNewIcon = feature => {
    const { id, icon, status, type, name } = feature;

    const el = document.createElement('div');

    const iconUrl = type === 'BLD' ? buildingIconURL : buildIconPath(icon);

    const iconEl = createPortal(
      <MapIconWrapper
        id={id}
        name={name}
        src={iconUrl}
        status={status.toLowerCase()}
      />,
      el,
      id,
    );

    const newMarker = new mapboxgl.Marker(el)
      .setLngLat([feature.lng, feature.lat])
      .addTo(window.map);

    newMarker.getElement().setAttribute('id', id);

    iconElRef.current.set(id, iconEl);
    markerRef.current.set(id, newMarker);

    return feature;
  };

  const drawMapIcons = () => {
    setValues({
      ...values,
      buildings: values.buildings.map(building => drawNewIcon(building)),
      assets: values.assets.map(asset =>
        asset.is_placed ? drawNewIcon(asset) : asset,
      ),
    });
  };

  const selectAllIcons = () => {
    buildingsRef.current.forEach(building => {
      building.selected = true;
      markerRef.current.get(building.id).remove();
    });
    setFieldValue('buildings', [...buildingsRef.current]);
  };

  const updateIconsOnScreen = e => {
    const items = [
      ...buildingsRef.current,
      ...assetsRef.current.filter(a => a.is_placed),
    ];
    const selectedIcon = e?.features[0]
      ? items.find(building => building.id === e.features[0].properties.id)
      : false;
    const deselectedIcons = items.filter(icon => icon.selected === true);

    deselectedIcons.forEach(icon => {
      const pointProps = window.map
        .queryRenderedFeatures()
        .filter(
          item =>
            item.properties.user_id === icon.id &&
            item.layer.id.includes('point-stroke'),
        )[0];

      [icon.lng, icon.lat] = pointProps.geometry.coordinates;
      new mapboxgl.Marker(markerRef.current.get(icon.id).getElement())
        .setLngLat([
          pointProps.geometry.coordinates[0],
          pointProps.geometry.coordinates[1],
        ])
        .addTo(window.map);
      icon.selected = false;
    });

    if (selectedIcon && !deselectedIcons.includes(selectedIcon)) {
      selectedIcon.selected = true;
      markerRef.current.get(selectedIcon.id).remove();
    }
    setFieldValue('buildings', [...buildingsRef.current]);
    setFieldValue('assets', [...assetsRef.current]);
    setEditingBuilding(null);
  };

  const clearState = () => {
    setFieldValue('lat', null);
    setFieldValue('lng', null);
    setFieldValue('coordinates', []);
    setIsTourOpen(true);
  };

  const handleChangeMode = e => {
    if (e.mode === 'draw_polygon') setIsTourOpen(false);
  };

  const handleSelectionChange = e => {
    updateIconsOnScreen(e);
  };

  const handleAssetUpdate = e => {
    const asset = assetsRef.current.find(
      asset => asset.id === e.features[0].properties.id,
    );
    if (asset) {
      [asset.lng, asset.lat] = e.features[0].geometry.coordinates;
      setFieldValue('assets', [...assetsRef.current]);
    }
  };

  const handleLocationUpdate = e => {
    if (e.features.length && e.features[0].geometry.type === 'Point') {
      if (e.features[0].properties.type === 'BLD') {
        const building = buildingsRef.current.find(
          building => building.id === e.features[0].properties.id,
        );
        [building.lng, building.lat] = e.features[0].geometry.coordinates;
        setFieldValue('buildings', [...buildingsRef.current]);
      } else {
        handleAssetUpdate(e);
      }
    } else {
      const centerCoords = centerCoordinates(e.features[0]);
      setFieldValue('coordinates', e.features[0].geometry.coordinates[0]);
      setFieldValue('lat', centerCoords[1]);
      setFieldValue('lng', centerCoords[0]);
    }
  };

  const handlePointDelete = e => {
    const deletedAsset = { ...e.features[0] };
    if (e.features[0].properties.type === 'BLD') {
      handleRemoveBuilding(deletedAsset.properties.id, e);
    } else {
      handleRemoveAsset(deletedAsset.properties.id);
    }
  };

  const handleLocationDelete = e => {
    if (e.features.length && e.features[0].geometry.type === 'Point') {
      handlePointDelete(e);
    } else if (e.features.length && e.features[0].geometry.type === 'Polygon') {
      disablePolygonButton(false);
      clearState();
    }
  };

  const removeAllEventListeners = () => {
    window.map.off('draw.modechange', handleChangeMode);
    window.map.off('draw.selectionchange', handleSelectionChange);
    window.map.off('draw.create', handleLocationCreate);
    window.map.off('draw.update', handleLocationUpdate);
    window.map.off('draw.delete', handleLocationDelete);
  };

  const addDrawControl = () => {
    if (!window.Draw) {
      const Draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          point: false,
          combine_features: false,
          trash: true,
        },
        userProperties: true,
        styles: DRAW_STYLES,
      });
      window.map.addControl(Draw, 'top-right');
      window.Draw = Draw;

      disablePolygonButton(values.coordinates.length > 0);
      const locationFeature =
        values.coordinates.length > 0
          ? {
              id: 'location-polygon',
              geometry: {
                coordinates: [values.coordinates],
                type: 'Polygon',
              },
              properties: values,
              type: 'Feature',
            }
          : null;
      const buildingFeatures = values.buildings.map(b => ({
        id: b.id,
        geometry: {
          coordinates: [b.lng, b.lat],
          type: 'Point',
        },
        properties: b,
        type: 'Feature',
      }));
      const assetFeatures = values.assets
        .filter(a => a.is_placed)
        .map(a => ({
          geometry: {
            coordinates: [a.lng, a.lat],
            type: 'Point',
          },
          properties: a,
          type: 'Feature',
        }));
      const features = [...buildingFeatures, ...assetFeatures];
      if (locationFeature) features.push(locationFeature);
      window.Draw.set({
        type: 'FeatureCollection',
        features,
      });
      if (!isLocationClone) window.Draw.changeMode('simple_select');
      else {
        window.Draw.changeMode('simple_select', {
          featureIds: ['location-polygon'].concat(
            buildingFeatures.map(b => b.id),
          ),
        });
        setTimeout(() => selectAllIcons(), 100);
      }
      window.map.on('draw.modechange', handleChangeMode);
      window.map.on('draw.selectionchange', handleSelectionChange);
      window.map.on('draw.create', handleLocationCreate);
      window.map.on('draw.update', handleLocationUpdate);
      window.map.on('draw.delete', handleLocationDelete);
    }
  };

  const addTour = () => {
    setTimeout(() => {
      setIsTourOpen(true);
    }, 100);
  };

  const removeDrawControl = () => {
    if (!window.Draw || !window.map?.hasControl(window.Draw)) return;
    window.map.removeControl(window.Draw);
    window.Draw = null;
  };

  useEffect(() => {
    window.map.visibleMarkers?.forEach(icon => icon.marker.remove());

    addDrawControl();

    if (!values.id && !isLocationClone) addTour();

    const filterOut = ['!=', ['get', 'id'], values.id];
    window.map.setFilter('locations_layer', filterOut);
    window.map.setFilter('locations_outline', filterOut);
    window.map.setFilter('locations_label', filterOut);
    window.map.setLayoutProperty('unclustered-point', 'visibility', 'none');
    window.map.setLayoutProperty('clusters', 'visibility', 'none');
    window.map.setLayoutProperty('unclustered_point_far', 'visibility', 'none');
    window.map.setLayoutProperty('cluster-count', 'visibility', 'none');
    drawMapIcons();

    const markers = markerRef.current;
    return () => {
      buildingsRef.current.forEach(icon => {
        markers.get(icon.id)?.remove();
        markers.delete(icon.id);
      });
      assetsRef.current.forEach(icon => {
        markers.get(icon.id)?.remove();
        markers.delete(icon.id);
      });

      clearState();

      if (!window.map?.isStyleLoaded()) {
        window.Draw = null;
        return;
      }

      removeDrawControl();
      removeAllEventListeners();

      window.map?.setFilter('locations_layer', null);
      window.map?.setFilter('locations_outline', null);
      window.map?.setFilter('locations_label', null);
      window.map?.setLayoutProperty(
        'unclustered-point',
        'visibility',
        'visible',
      );
      window.map?.setLayoutProperty('clusters', 'visibility', 'visible');
      window.map?.setLayoutProperty(
        'unclustered_point_far',
        'visibility',
        'visible',
      );
      window.map?.setLayoutProperty('cluster-count', 'visibility', 'visible');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disableForm = useMemo(
    () => !values.lat || delLoading,
    [delLoading, values.lat],
  );

  const replaceBuilding = building => {
    const newBuildings = values.buildings;
    const editObj = newBuildings.find(b => b.id === building.id);
    const index = newBuildings.indexOf(editObj);
    newBuildings[index] = building;
    setValues({ ...values, buildings: newBuildings });
  };

  const handleRemoveAsset = assetId => {
    const assetToRemove = assetsRef.current.find(asset => asset.id === assetId);
    markerRef.current.get(assetToRemove.id)?.remove();
    markerRef.current.delete(assetToRemove.id);
    assetToRemove.is_placed = false;
    assetToRemove.selected = false;
    setFieldValue('assets', [...assetsRef.current]);
    const mapFeature = window.Draw?.getAll().features.find(
      f => f.properties.id === assetId,
    );
    if (mapFeature) window.Draw.delete([mapFeature.id]);
  };

  const handleRemoveBuilding = (buildingId, e, ignoreConfirmation) => {
    const buildingToRemove = buildingsRef.current.find(
      building => building.id === buildingId,
    );
    const removeBuilding = mode => {
      markerRef.current.get(buildingToRemove.id)?.remove();
      markerRef.current.delete(buildingToRemove.id);
      setFieldTouched(
        `buildings.${buildingsRef.current.indexOf(buildingToRemove)}.name`,
        false,
      );
      setFieldValue(
        'buildings',
        buildingsRef.current.filter(building => building.id !== buildingId),
      );
      if (mode)
        setFieldValue(
          'deleted',
          values.deleted.concat({ id: buildingId, mode }),
        );
      const mapFeature = window.Draw?.getAll().features.find(
        f => f.properties.id === buildingId,
      );
      if (mapFeature) window.Draw.delete([mapFeature.id]);
    };
    if (ignoreConfirmation || !values.id) return removeBuilding();
    if (!buildingToRemove.floors?.length) return removeBuilding('only');
    openConfirmation({
      message: t('tools_menu.location.delete_building_confirm', {
        building: buildingToRemove.name,
      }),
      options: [
        {
          onClick: () => removeBuilding('only'),
          label: t('tools_menu.location.delete_building_only'),
          warning: true,
        },
        {
          onClick: () => removeBuilding('all'),
          label: t('tools_menu.location.delete_building_all'),
          warning: true,
        },
      ],
      handleClose: () => {
        if (e) {
          const feature = {
            geometry: {
              coordinates: [buildingToRemove.lng, buildingToRemove.lat],
              type: 'Point',
            },
            properties: buildingToRemove,
            type: 'Feature',
          };
          window.Draw.add(feature);
          setTimeout(() => updateIconsOnScreen(e), 500);
        }
        setTimeout(
          () =>
            document
              .querySelector('body')
              .classList.remove('modal-backdrop', 'modal', 'modal-open'),
          1000,
        );
      },
    });
  };

  const handleDropMaster = (id, dropResult) => {
    const evt = e => {
      const temp = { ...dropResult.asset };
      temp.is_placed = true;
      temp.lat = e.lngLat.lat;
      temp.lng = e.lngLat.lng;

      const feature = {
        geometry: {
          coordinates: [e.lngLat.lng, e.lngLat.lat],
          type: 'Point',
        },
        properties: temp,
        type: 'Feature',
      };

      window.Draw.add(feature);

      const asset = values.assets.find(a => a.id === temp.id);
      const index = values.assets.indexOf(asset);
      values.assets[index] = drawNewIcon(temp);
      setFieldValue('assets', [...values.assets]);

      window.map.off('mousemove', evt);
    };

    window.map.on('mousemove', evt);
  };

  const delAction = async mode => {
    try {
      setDelLoading(true);
      await delCRUD({
        entity: 'location',
        id: values.id,
        params: { mode },
      });
      openToast(t('tools_menu.location.delete_success'));
      popPage();
      queryClient.removeQueries({
        queryKey: 'locationsCache',
        exact: true,
      });
      refreshMap();
    } catch (e) {
      console.error(e);
      if (e.response.status === 409) {
        setTimeout(() => {
          setDelLoading(true);
          openConfirmation({
            title: t('tools_menu.location.delete_modal_title'),
            message: e.response.data.message.includes('table "ticket"')
              ? t('tools_menu.location.delete_modal_ticket')
              : t('tools_menu.location.delete_modal_conflict'),
            actionLabel: t('modal.ok'),
            handleAction: () => setDelLoading(false),
            preventClickOutside: true,
            hideCancel: true,
          });
        }, 100);
      } else {
        openToast(t('tools_menu.location.delete_error'), 'error');
      }
    } finally {
      setDelLoading(false);
    }
  };

  const handleDelete = () => {
    openConfirmation({
      message: t('tools_menu.location.delete_location_confirm', {
        location: values.name,
      }),
      options: [
        {
          onClick: async () => delAction('only'),
          label: t('tools_menu.location.delete_location_only'),
          warning: true,
          minWidth: '689px',
        },
        {
          onClick: async () => delAction('all'),
          label: t('tools_menu.location.delete_location_all'),
          warning: true,
          minWidth: '689px',
        },
      ],
      preventClickOutside: true,
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      alignItems="center"
      paddingX={2}
      paddingTop="10px"
      sx={{ pointerEvents: readyBuildingRef.current && 'none' }}
    >
      <Joyride
        steps={steps}
        run={isTourOpen}
        spotlightClicks
        disableOverlayClose
        styles={{
          buttonNext: {
            display: 'none',
          },
          buttonClose: {
            display: 'none',
          },
          tooltipContainer: {
            marginBottom: -15,
          },
        }}
      />
      <Form>
        <Typography textAlign="left" sx={{ whiteSpace: 'break-spaces' }}>
          {t('tools_menu.location.subtitle')}
        </Typography>
        <CustomFormControl
          id="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          errors={errors}
          touched={touched}
          label={t('tools_menu.location.name')}
          disabled={disableForm}
          maxLength={50}
        />

        <CustomFormControl
          id="description"
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          touched={touched}
          label={t('tools_menu.location.description')}
          disabled={disableForm}
          maxLength={255}
        />

        <UserGroupSelect
          id="user_group"
          resetForm={resetForm}
          initialValues={initialValues}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          handleBlur={handleBlur}
          touched={touched}
          errors={errors}
          disabled={disableForm}
          value={values.user_group}
        />

        <MapBuildingsManagement
          buildings={values.buildings}
          disabled={disableForm}
          setFieldValue={setFieldValue}
          replaceBuilding={replaceBuilding}
          handleRemoveBuilding={handleRemoveBuilding}
          updateIconsOnScreen={updateIconsOnScreen}
          editingBuilding={editingBuilding}
          setEditingBuilding={setEditingBuilding}
          errors={errors}
          touched={touched}
          handleBlur={handleBlur}
          handleChange={handleChange}
          setFieldTouched={setFieldTouched}
          readyBuildingRef={readyBuildingRef}
          iconElRef={iconElRef}
          markerRef={markerRef}
        />

        {values.id && (
          <DnDAssetsList
            didDrop={handleDropMaster}
            editing={values}
            popPage={popPage}
            disabled={disableForm}
            iconElRef={iconElRef}
          />
        )}
      </Form>

      <Stack flexWrap="wrap" paddingTop={values.id && '20px'}>
        {values.id && (
          <ActionButton
            label={t('tools_menu.location.delete_location_label')}
            onClick={handleDelete}
            warning
            disabled={disableForm}
            margin="0px"
          />
        )}
        <ActionButtonsPanel
          cancelLabel={t('buttons.cancel')}
          confirmLabel={
            isSubmitting || loading
              ? t('buttons.loading_save')
              : t('buttons.save')
          }
          confirmAction={async () => {
            setLoading(true);
            setTimeout(async () => {
              try {
                await submitForm();
              } catch (error) {
                console.error('Error:', error);
              } finally {
                setTimeout(() => setLoading(false), 100);
              }
            }, 0);
          }}
          cancelAction={() => {
            if (!dirty) popPage();
            else
              openConfirmation({
                message: t('modal.discard.description'),
                handleAction: popPage,
                title: t('modal.cancel'),
              });
          }}
          disabled={
            !isValid ||
            isSubmitting ||
            disableForm ||
            loading ||
            editingBuilding
          }
          cancelDisabled={isSubmitting || loading || delLoading}
          justifyContent="center"
        />
      </Stack>
    </Box>
  );
};

ManageLocationForm.propTypes = {
  resetForm: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  isValid: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  submitForm: PropTypes.func.isRequired,
  popPage: PropTypes.func.isRequired,
  touched: PropTypes.object.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
};

export default ManageLocationForm;
